/**
 * Mobile only: flag the single event teaser (`template-parts/teaser/event.php`,
 * root `.teaser-event`) the reader is currently on with `is-inview`, so CSS can
 * react to just that one card.
 *
 * Only ONE teaser carries `is-inview` at a time: on every intersection update we
 * pick the observed card with the greatest viewport coverage (as long as it
 * clears MIN_RATIO) and move the class there. This avoids the earlier behaviour
 * where every card past a fixed 75% cutoff lit up at once — with square,
 * full-width tiles two cards are frequently ≥75% visible together, so two lit up
 * simultaneously.
 *
 * Desktop is left untouched — the observer is only attached below the `medium`
 * breakpoint (768px, see settings.sass) and is torn down again if the viewport
 * grows past it. Teasers appended later by load-more (src/js/loadMore.js) are
 * picked up via a MutationObserver on the load-more holder(s).
 */

const SELECTOR = '.teaser-event,.teaser-link'
const IN_VIEW = 'is-inview'
// Minimum coverage before a card is eligible to be "the active one". Cards below
// this never win, so scrolling through the gaps between tiles or into the footer
// clears the state instead of leaving a barely-visible card lit. Kept low enough
// that the most-covered card always clears it during the hand-off between two
// adjacent tiles, so exactly one card stays active while scrolling.
const MIN_RATIO = 0.6
// Fine-grained thresholds so the observer reports ratio changes as cards scroll,
// letting us compare cards and pick the most-visible one (a single 0.75 cutoff
// would only tell us "past/not past", not which card is most in view).
const THRESHOLDS = [0, 0.25, 0.5, 0.6, 0.75, 0.9, 1]
const MOBILE = window.matchMedia('(max-width: 767px)')

let io = null
let mo = null
let active = null
const ratios = new Map()

// Winner = the observed card with the greatest viewport coverage, provided it
// clears MIN_RATIO. Ties (two cards equally covered, e.g. straddling the centre)
// go to the upper card in DOM order, giving a single clean hand-off with no flicker.
const refresh = () => {
  let winner = null
  let best = 0
  for (const [el, ratio] of ratios) {
    if (ratio > best) {
      best = ratio
      winner = el
    }
  }
  if (best < MIN_RATIO) winner = null

  if (winner === active) return
  if (active) active.classList.remove(IN_VIEW)
  if (winner) winner.classList.add(IN_VIEW)
  active = winner
}

const onIntersect = (entries) => {
  for (const entry of entries) {
    ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0)
  }
  refresh()
}

const enable = () => {
  if (io || typeof IntersectionObserver !== 'function') return

  io = new IntersectionObserver(onIntersect, { threshold: THRESHOLDS })
  document.querySelectorAll(SELECTOR).forEach((el) => io.observe(el))

  // Observe teasers that load-more appends after the initial render.
  const holders = document.querySelectorAll('[data-load-more-holder]')
  if (holders.length && typeof MutationObserver === 'function') {
    mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue
          if (node.matches?.(SELECTOR)) io.observe(node)
          node.querySelectorAll?.(SELECTOR).forEach((el) => io.observe(el))
        }
      }
    })
    holders.forEach((h) => mo.observe(h, { childList: true, subtree: true }))
  }
}

const disable = () => {
  if (mo) { mo.disconnect(); mo = null }
  if (io) { io.disconnect(); io = null }
  ratios.clear()
  active = null
  document.querySelectorAll(`${SELECTOR}.${IN_VIEW}`).forEach((el) => el.classList.remove(IN_VIEW))
}

const sync = () => (MOBILE.matches ? enable() : disable())

sync()
MOBILE.addEventListener('change', sync)
